import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Calendar, MessageSquare, BarChart3, AlertCircle, Loader2, TableIcon, BarChart, ArrowRight, Download, ChevronDown, RefreshCw, Database } from 'lucide-react';
import ChatMessage from '../ChatMessage';
import DataChart from '../DataChart';
import { conversationAPI } from '../../services/api';
import type { ChartSuggestion } from '../../types';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  metadata?: {
    sql?: string;
    tables?: string[];
    error?: string;
    // Query results stored in conversation history (up to 100 rows)
    queryResults?: {
      data: any[];
      totalRowCount: number;
      isLimited: boolean;
      columns: string[];
    };
    // Add query response data from visualizations
    queryResponse?: {
      sql: string;
      columns: string[];
      allColumns?: string[];
      defaultVisibleColumns?: string[];
      columnAliases?: Record<string, string>;
      currencyColumns?: string[];
      results: any[];
      rowCount: number;
      totalRowCount?: number;
      isLimited?: boolean;
      explanation?: string;
      suggestedFollowUps?: string[];
      chartSuggestion?: any;
    };
  };
  visualization?: {
    visualizationId: string;
    chartConfig?: any;
    visibleColumns?: string[];
    columnAliases?: Record<string, string>;
  };
}

interface ConversationData {
  conversationId: string;
  title?: string;
  messages: Message[];
  context: {
    tables: string[];
    timeframe?: string;
    filters?: Record<string, any>;
  };
  metadata?: {
    firstQuery?: string;
    messageCount: number;
    hasCharts: boolean;
    hasErrors: boolean;
  };
  isActive: boolean;
  lastActivity: string;
  createdAt: string;
}

interface ConversationViewerProps {
  conversationId: string;
  onFollowUpClick?: (question: string) => void;
}

export const ConversationViewer: React.FC<ConversationViewerProps> = ({
  conversationId,
  onFollowUpClick
}) => {
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visualizationErrors, setVisualizationErrors] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [rerunningQueries, setRerunningQueries] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await conversationAPI.getFullConversation(conversationId);
        setConversation(data);
      } catch (err) {
        console.error('Failed to fetch conversation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    if (conversationId) {
      fetchConversation();
    }
  }, [conversationId]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  const handleContinueConversation = () => {
    if (!conversation) return;
    
    // Transform conversation messages to the format expected by QueryPage
    const queryPageMessages = conversation.messages.map((message, index) => ({
      id: `${message.timestamp}-${index}`,
      type: message.role as 'user' | 'assistant',
      content: message.content,
      timestamp: new Date(message.timestamp),
      queryResponse: message.metadata?.queryResults ? {
        sql: message.metadata.sql || '',
        columns: message.metadata.queryResults.columns || [],
        allColumns: message.metadata.queryResults.columns || [],
        defaultVisibleColumns: message.visualization?.visibleColumns || message.metadata.queryResults.columns || [],
        columnAliases: message.visualization?.columnAliases || {},
        currencyColumns: [],
        results: message.metadata.queryResults.data || [],
        rowCount: message.metadata.queryResults.data?.length || 0,
        totalRowCount: message.metadata.queryResults.totalRowCount || 0,
        isLimited: message.metadata.queryResults.isLimited || false,
        explanation: message.content,
        suggestedFollowUps: [],
        chartSuggestion: message.visualization?.chartConfig,
      } : message.metadata?.queryResponse ? {
        // Fallback to queryResponse if it exists (for compatibility)
        sql: message.metadata.queryResponse.sql,
        columns: message.metadata.queryResponse.columns,
        allColumns: message.metadata.queryResponse.allColumns,
        defaultVisibleColumns: message.metadata.queryResponse.defaultVisibleColumns,
        columnAliases: message.metadata.queryResponse.columnAliases,
        currencyColumns: message.metadata.queryResponse.currencyColumns,
        results: message.metadata.queryResponse.results,
        rowCount: message.metadata.queryResponse.rowCount,
        totalRowCount: message.metadata.queryResponse.totalRowCount,
        isLimited: message.metadata.queryResponse.isLimited,
        explanation: message.metadata.queryResponse.explanation,
        suggestedFollowUps: message.metadata.queryResponse.suggestedFollowUps,
        chartSuggestion: message.metadata.queryResponse.chartSuggestion,
      } : undefined,
    }));

    // Navigate to QueryPage with conversation context
    navigate('/query', {
      state: {
        continueConversation: true,
        conversationId: conversation.conversationId,
        messages: queryPageMessages,
        context: conversation.context,
      }
    });
  };

  const handleExport = async (format: 'markdown' | 'pdf' | 'json' | 'csv') => {
    if (!conversation) return;
    
    try {
      setIsExporting(true);
      await conversationAPI.exportConversation(conversation.conversationId, format);
    } catch (err) {
      console.error('Failed to export conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to export conversation');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRerunQuery = (messageIndex: number, sql: string) => {
    if (!conversation) return;
    
    // Navigate to query page with the SQL to re-run
    navigate('/query', {
      state: {
        rerunQuery: true,
        sql: sql,
        conversationId: conversation.conversationId,
        originalMessageIndex: messageIndex
      }
    });
  };

  const transformMessageForChatMessage = (message: Message) => {
    // Transform the message to match ChatMessage component expectations
    let queryResponse = undefined;
    
    // If the message has SQL metadata, it's likely an assistant response with query results
    if (message.metadata?.sql && message.role === 'assistant') {
      // Use stored query results if available
      const storedResults = message.metadata.queryResults;
      
      queryResponse = {
        sql: message.metadata.sql,
        columns: storedResults?.columns || message.visualization?.visibleColumns || [],
        allColumns: storedResults?.columns || message.visualization?.visibleColumns || [],
        defaultVisibleColumns: message.visualization?.visibleColumns || storedResults?.columns || [],
        columnAliases: message.visualization?.columnAliases || {},
        currencyColumns: [],
        results: storedResults?.data || [], // Use stored results from conversation history
        rowCount: storedResults?.data?.length || 0,
        totalRowCount: storedResults?.totalRowCount || 0,
        isLimited: storedResults?.isLimited || false,
        explanation: message.content, // Use the message content as explanation
        suggestedFollowUps: [],
        chartSuggestion: message.visualization?.chartConfig,
        // Add flag to indicate if more data is available
        hasMoreData: storedResults?.isLimited || false
      };
    } else if (message.metadata?.queryResponse && message.role === 'assistant') {
      // Fallback: Check for queryResponse format (for compatibility)
      queryResponse = message.metadata.queryResponse;
    }
    
    return {
      type: message.role as 'user' | 'assistant',
      content: message.content,
      timestamp: new Date(message.timestamp),
      queryResponse
    };
  };

  // Render visualization recreation for messages with chart configurations
  const renderVisualizationRecreation = (message: Message) => {
    if (!message.visualization?.chartConfig || message.role !== 'assistant') {
      return null;
    }

    // Create a unique key for this visualization
    const vizKey = `${message.timestamp}-viz`;
    const hasError = visualizationErrors.has(vizKey);

    try {
      const chartConfig = message.visualization.chartConfig as ChartSuggestion;
      
      // Check if we have a valid chart configuration
      if (!chartConfig || chartConfig.type === 'none') {
        return null;
      }

      return (
        <div className="mt-4 p-4 border rounded-lg bg-card">
          <div className="flex items-center gap-2 mb-3">
            <BarChart className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Visualization Recreation</span>
            <Badge variant="secondary" className="text-xs">
              {chartConfig.type.replace('-', ' ')}
            </Badge>
          </div>
          
          {hasError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <div>
                <h4 className="font-medium">Visualization Recreation Failed</h4>
                <p className="text-sm mt-1">
                  There was an error recreating this visualization. The chart configuration may be incomplete or corrupted.
                </p>
              </div>
            </Alert>
          ) : (
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <div>
                <p className="text-sm">
                  This is a recreation of the original visualization configuration. The actual query data is not stored in conversation history for security and storage efficiency.
                </p>
              </div>
            </Alert>
          )}

          <div className="bg-muted/30 rounded-lg p-4">
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">Chart Type:</span>
                <Badge variant="outline">{chartConfig.type}</Badge>
              </div>
              
              {chartConfig.xAxis && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">X-Axis:</span>
                  <code className="px-2 py-1 bg-background rounded text-xs">{chartConfig.xAxis}</code>
                </div>
              )}
              
              {chartConfig.yAxis && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Y-Axis:</span>
                  <code className="px-2 py-1 bg-background rounded text-xs">
                    {Array.isArray(chartConfig.yAxis) ? chartConfig.yAxis.join(', ') : chartConfig.yAxis}
                  </code>
                </div>
              )}
              
              {chartConfig.groupBy && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Group By:</span>
                  <code className="px-2 py-1 bg-background rounded text-xs">{chartConfig.groupBy}</code>
                </div>
              )}
              
              {chartConfig.title && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">Title:</span>
                  <span className="text-xs">{chartConfig.title}</span>
                </div>
              )}
              
              {chartConfig.rationale && (
                <div className="space-y-1">
                  <span className="font-medium">Rationale:</span>
                  <p className="text-xs text-muted-foreground">{chartConfig.rationale}</p>
                </div>
              )}
            </div>
          </div>

          {message.visualization?.visibleColumns && message.visualization.visibleColumns.length > 0 && (
            <div className="mt-3 space-y-2">
              <span className="text-sm font-medium">Visible Columns:</span>
              <div className="flex flex-wrap gap-1">
                {message.visualization.visibleColumns.map((column, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {message.visualization?.columnAliases?.[column] || column}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Show sample data structure note if we have metadata */}
          {message.metadata?.queryResponse && (
            <div className="mt-3 p-3 bg-muted/50 rounded text-xs">
              <div className="font-medium mb-1">Original Query Context:</div>
              <div className="space-y-1 text-muted-foreground">
                {message.metadata.queryResponse.rowCount > 0 && (
                  <div>• Result set contained {message.metadata.queryResponse.rowCount} rows</div>
                )}
                {message.metadata.queryResponse.columns.length > 0 && (
                  <div>• Columns: {message.metadata.queryResponse.columns.join(', ')}</div>
                )}
                {message.metadata.sql && (
                  <div className="mt-2">
                    <div className="font-medium">SQL Query:</div>
                    <code className="block mt-1 p-2 bg-background rounded text-xs font-mono">
                      {message.metadata.sql.length > 200 
                        ? message.metadata.sql.substring(0, 200) + '...' 
                        : message.metadata.sql
                      }
                    </code>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Error rendering visualization recreation:', error);
      
      // Track this error to prevent repeated error rendering
      setVisualizationErrors(prev => new Set([...prev, vizKey]));
      
      return (
        <div className="mt-4 p-4 border rounded-lg bg-card">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <div>
              <h4 className="font-medium">Visualization Recreation Error</h4>
              <p className="text-sm mt-1">
                There was an error recreating this visualization. The chart configuration may be incomplete or corrupted.
              </p>
            </div>
          </Alert>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading conversation...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <div>
          <h4 className="font-medium">Error loading conversation</h4>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
      </Alert>
    );
  }

  if (!conversation) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <div>
          <h4 className="font-medium">Conversation not found</h4>
          <p className="text-sm text-muted-foreground mt-1">
            The requested conversation could not be found or you don't have permission to view it.
          </p>
        </div>
      </Alert>
    );
  }

  if (!conversation.messages || conversation.messages.length === 0) {
    return (
      <div className="space-y-6">
        {/* Conversation Header */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold">
                  {conversation.title || 'Untitled Conversation'}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>Created {formatDate(conversation.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <MessageSquare className="h-4 w-4" />
                    <span>{conversation.metadata?.messageCount || 0} messages</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleContinueConversation}
                  className="flex items-center gap-2"
                >
                  <ArrowRight className="h-4 w-4" />
                  Continue Conversation
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      disabled={isExporting}
                      className="flex items-center gap-2"
                    >
                      {isExporting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4" />
                      )}
                      Export
                      {!isExporting && <ChevronDown className="h-4 w-4" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport('markdown')}>
                      Export as Markdown
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('pdf')}>
                      Export as PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('json')}>
                      Export as JSON
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport('csv')}>
                      Export as CSV
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {conversation.metadata?.hasCharts && (
                  <Badge variant="secondary" className="text-xs">
                    <BarChart3 className="h-3 w-3 mr-1" />
                    Charts
                  </Badge>
                )}
                {conversation.metadata?.hasErrors && (
                  <Badge variant="destructive" className="text-xs">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Errors
                  </Badge>
                )}
                {!conversation.isActive && (
                  <Badge variant="outline" className="text-xs">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Empty State */}
        <Alert>
          <MessageSquare className="h-4 w-4" />
          <div>
            <h4 className="font-medium">No messages yet</h4>
            <p className="text-sm text-muted-foreground mt-1">
              This conversation doesn't have any messages yet.
            </p>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Conversation Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-2xl font-bold">
                {conversation.title || 'Untitled Conversation'}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Created {formatDate(conversation.createdAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" />
                  <span>{conversation.messages.length} messages</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  <span>Last activity {formatDate(conversation.lastActivity)}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleContinueConversation}
                className="flex items-center gap-2"
              >
                <ArrowRight className="h-4 w-4" />
                Continue Conversation
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={isExporting}
                    className="flex items-center gap-2"
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Export
                    {!isExporting && <ChevronDown className="h-4 w-4" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport('markdown')}>
                    Export as Markdown
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('pdf')}>
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('json')}>
                    Export as JSON
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport('csv')}>
                    Export as CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {conversation.metadata?.hasCharts && (
                <Badge variant="secondary" className="text-xs">
                  <BarChart3 className="h-3 w-3 mr-1" />
                  Charts
                </Badge>
              )}
              {conversation.metadata?.hasErrors && (
                <Badge variant="destructive" className="text-xs">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Errors
                </Badge>
              )}
              {!conversation.isActive && (
                <Badge variant="outline" className="text-xs">
                  Inactive
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Messages */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {conversation.messages.map((message, index) => (
              <div key={`${message.timestamp}-${index}`}>
                <ChatMessage
                  message={transformMessageForChatMessage(message)}
                  onFollowUpClick={onFollowUpClick}
                  conversationId={conversation.conversationId}
                />
                {renderVisualizationRecreation(message)}
                {/* Show re-run query option if data was limited */}
                {message.metadata?.queryResults?.isLimited && message.metadata?.sql && (
                  <Alert className="mt-4">
                    <Database className="h-4 w-4" />
                    <div className="ml-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium">Limited Results Displayed</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Showing first 100 rows of {message.metadata.queryResults.totalRowCount} total rows. 
                            Re-run the query to see all results.
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRerunQuery(index, message.metadata?.sql || '')}
                          className="ml-4 flex items-center gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Re-run Query
                        </Button>
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConversationViewer;